import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, CardContent } from '../../Components/UI/card';
import { Button } from '../../Components/UI/button';
import { Mail, Check, Ban, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getExternalTaskDetailsAPI } from './TasksAPI';
import { getColorClass, getRelativeDateText, getTaskColor } from '../../Utils/Utilities';
import { BsWhatsapp } from 'react-icons/bs';
import { MdPhone } from 'react-icons/md';
import { AlertBox } from '../../Components/Common/AlertBox';
import { TaskCompleteDialog } from '../../Components/Common/TaskCompleteDialog';
import useIsMobile from '../../Hooks/useIsMobile';
import externalAPI from '../../Connect/ExternalAPI';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';

// const tskDetail = {
//     cardBase: "border border-gray-200 bg-white rounded-lg !pt-0",
//     cardPadding: "p-4",
//     inlineBadge: "inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-md text-sm",
//     smallBtn: "text-sm px-3 py-1.5 h-auto flex-1",
//     threadTag: "inline-flex items-center gap-2 bg-gray-100 rounded-md text-sm"
// };

const ExternalTasks = () => {
    const [searchParams] = useSearchParams();
    const agencyID = searchParams.get('agnID');
    const taskID = searchParams.get('tskID');
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const globalLoading = useSelector((state) => state.loading?.isLoading || false);
    
    const [taskData, setTaskData] = useState(null);
    const [connectionData, setConnectionData] = useState(null);
    const [agencyData, setAgencyData] = useState(null);
    const [error, setError] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({
        complete: false,
        cancel: false
    });

    useEffect(() => {
        const fetchTaskDetails = async () => {
            try {
                setError(null);

                if (!agencyID || !taskID) {
                    setError('Missing required parameters: agencyID and taskID');
                    return;
                }

                const response = await getExternalTaskDetailsAPI(
                    parseInt(agencyID),
                    parseInt(taskID)
                );

                if (response?.error === true) {
                    setError(response?.message || 'Failed to fetch task details');
                    toast.error('Error', { description: response?.message || 'Failed to fetch task details' });
                    return;
                }

                // Parse the updatePackage which contains the actual data
                if (response?.updatePackage) {
                    try {
                        const parsedPackage = JSON.parse(response.updatePackage);
                        
                        // Check for error in Table (first table has error codes)
                        if (parsedPackage?.Table && parsedPackage.Table[0]?.FnErrCode !== '-1') {
                            setError(parsedPackage.Table[0]?.ErrMsg || 'Task not found');
                        } else if (parsedPackage?.Table1 && Array.isArray(parsedPackage.Table1) && parsedPackage.Table1.length > 0) {
                            const task = parsedPackage.Table1[0];
                            const agency = parsedPackage?.Table2?.[0] || null;
                            const connection = parsedPackage?.Table3?.[0] || null;
                            
                            setTaskData(task);
                            setAgencyData(agency);
                            setConnectionData(connection);
                        } else {
                            setError('Task details not found');
                        }
                    } catch (parseErr) {
                        console.error('Error parsing updatePackage:', parseErr);
                        setError('Failed to parse task data');
                    }
                } else {
                    setError('Invalid response from server');
                }
            } catch (err) {
                console.error('Error fetching task details:', err);
                setError(err.message || 'Failed to fetch task details');
                toast.error('Error', { description: 'Failed to fetch task details' });
            }
        };

        fetchTaskDetails();
    }, [agencyID, taskID]);

    const createEmailLink = (email) => `mailto:${email?.trim()}`;

    const handleCallClick = async () => {
        try {
            const cleanPhone = connectionData.Mobile.replace(/\D/g, '');
            const phoneWithCode = `+${connectionData.CountryCode}${cleanPhone}`;
            
            if (isMobile) {
                window.location.href = `tel:${phoneWithCode}`;
                
                const response = await externalAPI.createContactLog(
                    parseInt(agencyID),
                    connectionData?.ConnID,
                    'Call',
                    taskData?.TaskID || 0
                );
                
                if (response?.error === true) {
                    toast.error('Error', { description: response?.message || 'Failed to log call record' });
                } else {
                    toast.success('Record logged.');
                }
            } else {
                try {
                    await navigator.clipboard.writeText(cleanPhone);
                    toast.success(`Phone (${cleanPhone}) copied to clipboard!`);
                } catch (err) {
                    console.error('Failed to copy: ', err);
                    toast.error('Failed to copy to clipboard');
                }
            }
        } catch (error) {
            console.error('Error logging call:', error);
            toast.error('Error', { description: error.message || 'Failed to log call record' });
        }
    };

    const handleWhatsAppClick = async () => {
        try {
            const cleanPhone = connectionData.Mobile.replace(/\D/g, '');
            
            const response = await externalAPI.createContactLog(
                parseInt(agencyID),
                connectionData?.ConnID,
                'Message',
                taskData?.TaskID || 0
            );
            
            if (response?.error === true) {
                toast.error('Error', { description: response?.message || 'Failed to log message record' });
                return;
            }
            
            window.open(`https://wa.me/${cleanPhone}`, '_blank');
            toast.success('Record logged.');
        } catch (error) {
            console.error('Error logging message:', error);
            toast.error('Error', { description: error.message || 'Failed to log message record' });
        }
    };

    const handleCompleteTask = async () => {
        try {
            setConfirmDialog(prev => ({ ...prev, complete: false }));
            
            const response = await externalAPI.completeTask(
                parseInt(agencyID),
                taskData?.ConnID || 0,
                taskData?.TaskID
            );
            
            if (response?.error === true) {
                toast.error('Error', { description: response?.message || 'Failed to complete task' });
                return;
            }
            
            toast.success('Task completed!', { duration: 3000 });
            // Optionally reload data
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            console.error('Error completing task:', err);
            toast.error('Error', { description: err.message || 'Failed to complete task' });
        }
    };

    const handleCancelTask = async () => {
        try {
            setConfirmDialog(prev => ({ ...prev, cancel: false }));
            
            const response = await externalAPI.cancelTask(
                parseInt(agencyID),
                taskData?.ConnID || 0,
                taskData?.TaskID
            );
            
            if (response?.error === true) {
                toast.error('Error', { description: response?.message || 'Failed to cancel task' });
                return;
            }
            
            toast.success('Task cancelled!', { duration: 3000 });
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            console.error('Error cancelling task:', err);
            toast.error('Error', { description: err.message || 'Failed to cancel task' });
        }
    };

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="space-y-4 text-center">
                    <p className="text-red-600 font-semibold text-lg">{error}</p>
                    <p className="text-sm text-gray-500">Please check the URL parameters and try again</p>
                </div>
            </div>
        );
    }

    if (!taskData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <p className="text-gray-600">No task data available</p>
            </div>
        );
    }

    const handleClose = () => {
        navigate(-1);
    };

    const businessName = connectionData?.BizName || connectionData?.ContactName || '';

    return (
        <Dialog open={true} onOpenChange={() => {}}>
            <DialogContent className={isMobile ? "w-full h-full max-w-full max-h-full p-0 flex flex-col gap-0 bg-[var(--background)]" : "sm:!max-w-[33vw] sm:!w-[43vw] sm:!min-h-[26vh] sm:!max-h-[72vh] flex flex-col gap-0 bg-[var(--background)] overflow-visible"}>
                <DialogHeader className={isMobile ? "border-b px-3" : "border-b pb-3"}>
                    <div className="flex items-center justify-between w-full">
                        {isMobile ? (
                            <>
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <button
                                        onClick={handleClose}
                                        className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                                    >
                                        <ArrowLeft className="h-[24px] w-[24px]" />
                                    </button>
                                    <DialogTitle className="font-medium">
                                        <div className="truncate">Task #{taskData?.TaskID || ''}</div>
                                    </DialogTitle>
                                </div>
                            </>
                        ) : (
                            <>
                                <DialogTitle className="text-xl">
                                    Task #{taskData?.TaskID || ''} {businessName &&
                                        ` - ${businessName}`
                                    }
                                </DialogTitle>
                                <DialogClose asChild><Button variant="ghost" className="h-8 w-8 p-0"><X className="h-4 w-4" /></Button></DialogClose>
                            </>
                        )}
                    </div>
                </DialogHeader>

                <div className={isMobile ? "overflow-y-auto p-3 max-h-[calc(100vh-250px)]" : "p-4 overflow-hidden h-full"}>
                    <div className={isMobile ? "space-y-4" : "space-y-6 overflow-y-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"}>
                        {/* Task Title */}
                        <div>
                            <h2 className={`${'text-lg'} font-semibold text-color`}>{taskData?.Task || 'Task'}</h2>
                            {/* {businessName && (
                                <p className="text-sm text-gray-600 mt-1">{businessName}</p>
                            )} */}
                        </div>
                        
                        {/* Contact Action Buttons */}
                        {connectionData && connectionData?.ConnID && connectionData?.ConnID !== 0 && connectionData?.Mobile && (
                            <div className={isMobile ? "flex gap-2" : "flex gap-3 w-full"}>
                                {connectionData?.Mobile && (
                                    <Button
                                        size="sm"
                                        className={isMobile 
                                            ? "h-auto py-1.5 px-1.5 w-full text-xs bg-sahi-blue border border-gray-300 text-white hover:bg-gray-200 flex items-center gap-1 whitespace-nowrap" 
                                            : "h-auto py-1.5 px-1.5 w-full text-xs bg-white border border-gray-300 text-foreground hover:bg-gray-200 flex items-center"
                                        }
                                        onClick={handleCallClick}
                                    >
                                        <MdPhone className="h-4 w-4" />
                                        <span className="text-sm font-semibold">Call</span>
                                    </Button>
                                )}

                                {connectionData?.Mobile && (
                                    <Button
                                        size="sm"
                                        className={isMobile 
                                            ? "h-auto py-1.5 px-1.5 w-full text-xs bg-sahi-green border border-gray-300 text-white hover:bg-gray-200 flex items-center gap-1 whitespace-nowrap" 
                                            : "h-auto py-1.5 px-1.5 w-full text-xs bg-white border border-gray-300 text-foreground hover:bg-gray-200 flex items-center"
                                        }
                                        onClick={handleWhatsAppClick}
                                    >
                                        <BsWhatsapp className={`h-4 w-4 ${!isMobile ? "text-sahi-green" : ""}`} />
                                        <span className="text-sm font-semibold">Message</span>
                                    </Button>
                                )}

                                {/* {connectionData?.Email && (
                                    <Button
                                        size="sm"
                                        className={isMobile 
                                            ? "h-auto py-1.5 px-2 text-xs text-white hover:bg-blue-700 flex items-center gap-1 whitespace-nowrap" 
                                            : "h-auto py-1.5 px-1.5 w-full text-xs bg-white border border-gray-300 text-foreground hover:bg-gray-200 flex items-center"
                                        }
                                        onClick={() => window.open(`mailto:${connectionData.Email.trim()}`)}
                                    >
                                        <Mail className={`h-4 w-4 ${!isMobile ? "text-sahi-orange" : ""}`} />
                                        <span className="text-sm font-semibold">Email</span>
                                    </Button>
                                )} */}
                            </div>
                        )}

                        {/* Task Information Card */}
                        <Card className="border border-gray-200 bg-white rounded-lg !pt-0">
                            <CardContent className={isMobile ? 'p-3' : "px-3 p-3"}>
                                <div className="space-y-3">
                                    {/* <h3 className="font-semibold text-gray-900">Task Information</h3> */}
                                    <div className="flex flex-wrap gap-2">
                                        {/* Business */}
                                        {connectionData?.BizName && (
                                            <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-md text-sm">
                                                <span className="text-gray-700">Business:</span>
                                                <span className="font-semibold text-gray-900">{connectionData.BizName}</span>
                                            </div>
                                        )}

                                        {/* Task Type */}
                                        {taskData?.TaskType && (
                                            <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-md text-sm">
                                                <span className="text-gray-700">Type:</span>
                                                <span className="font-semibold text-gray-900">{taskData.TaskType}</span>
                                            </div>
                                        )}

                                        {/* Due Date */}
                                        {taskData?.DueDT && (
                                            <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-md text-sm">
                                                <span className="text-gray-700">Finish by:</span>
                                                <span className={`font-semibold ${getColorClass(getTaskColor(taskData.DueDT))}`}>{getRelativeDateText(taskData.DueDT)}</span>
                                            </div>
                                        )}

                                        {/* Status */}
                                        {taskData?.Status !== undefined && (
                                            <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-md text-sm">
                                                <span className="text-gray-700">Status:</span>
                                                <span className="font-semibold text-gray-900">{taskData.Status === 0 ? 'Cancelled' : taskData.Status === 1 ? 'Open' : 'Completed'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Action Buttons for Mobile */}
                        {isMobile && taskData.Status === 1 && (
                            <div className="flex gap-3">
                                <Button onClick={() => setConfirmDialog(prev => ({ ...prev, complete: true }))} disabled={globalLoading} className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green w-fit">
                                    <Check className="h-4 w-4" /> Complete Task
                                </Button>
                                <Button variant="outline" onClick={() => setConfirmDialog(prev => ({ ...prev, cancel: true }))} disabled={globalLoading} className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red w-fit text-primary-foreground">
                                    <Ban className="h-4 w-4" /> Cancel Task
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons for Desktop */}
                {!isMobile && taskData.Status === 1 && (
                    <div className="bg-background rounded-lg px-4 pb-3 flex flex-wrap gap-3">
                        <Button onClick={() => setConfirmDialog(prev => ({ ...prev, complete: true }))} disabled={globalLoading} className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green sm:flex-none sm:min-w-[140px]">
                            <Check className="h-4 w-4" /> Complete Task
                        </Button>
                        <Button variant="outline" onClick={() => setConfirmDialog(prev => ({ ...prev, cancel: true }))} disabled={globalLoading} className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red sm:flex-none sm:min-w-[140px] text-primary-foreground">
                            <Ban className="h-4 w-4" /> Cancel Task
                        </Button>
                    </div>
                )}

                {/* Footer */}
                <div className={`text-center pb-4 ${isMobile ? "mt-8" : "mt-4"}`}>
                    <p className="text-sm text-gray-500">
                        Powered by{' '}
                        <a href="https://sahicrm.com/" className="text-sahi-green hover:underline">
                            SahiClub.com
                        </a>
                    </p>
                </div>
            </DialogContent>

            {/* Complete Task Confirmation */}
            <AlertBox
                open={confirmDialog.complete}
                onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, complete: open }))}
                title="Complete Task"
                content={
                    <div>
                        <p className="mb-2">Task: <span className="font-medium text-sahi-primary-color">{taskData?.Task}</span></p>
                        <p className="mb-1">Are you sure you want to mark this task as completed?</p>
                    </div>
                }
                className={!isMobile ? "w-[400px]" : ""}
                actions={[
                    { label: "Cancel", value: 'cancel', variant: "cancel", icon: <X className="h-4 w-4" /> },
                    { label: "Complete", value: 'complete', icon: <Check className="h-4 w-4" /> }
                ]}
                onAction={(val) => {
                    if (val === 'complete') handleCompleteTask();
                }}
            />

            {/* Cancel Task Confirmation */}
            <TaskCompleteDialog
                mode="cancel"
                open={confirmDialog.cancel}
                onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, cancel: open }))}
                task={taskData}
                className={!isMobile ? "w-[400px]" : ""}
                onAction={(val) => {
                    if (val === 'yes') handleCancelTask();
                }}
            />
        </Dialog>
    );
};

export default ExternalTasks;
